import { useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { RiDeleteBin6Line } from "react-icons/ri";
import defaultImage from "../../../../utils/DefaultImage";

const ExistingWinesOrder = ({
    isVisible,
    selectedWines,
    handleDirectQuantityInput,
    handleRemoveWine,
    cleanImageUrl,
    title = "Vini finali per la proposta",
    canRemove = true
}) => {
    const [selectedImage, setSelectedImage] = useState(null);

    // Helper to get formatted display quantity
    const getDisplayQuantity = (quantity) => {
        if (quantity === undefined || quantity === null) return 1;
        return quantity;
    };

    // Prevent non-numeric characters, decimal points, and leading zero
    const handleQuantityKeyDown = (e) => {
        if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
            e.preventDefault();
        }
        if (e.key === "0" && (!e.target.value || e.target.value === "")) {
            e.preventDefault();
        }
    };

    // Quantity change handler
    const handleQuantityChange = (index, value) => {
        handleDirectQuantityInput(index, value);
    };

    // Quantity blur handler - defaults to 1 if empty or invalid
    const handleQuantityBlur = (index, currentQty) => {
        if (!currentQty || currentQty === "" || Number(currentQty) < 1) {
            handleDirectQuantityInput(index, "1");
        }
    };

    return (
        <div className={`w-full bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-4 pb-4 rounded-xl shadow space-y-4 ${isVisible ? "block" : "hidden"}`}>
            <h4 className="text-xl font-bold poppins-medium text-[#303030] mb-2">{title}</h4>

            {/* Selected Proposal Wines Table */}
            <div className="overflow-x-auto">
                <table className="md:w-full w-[750px] text-left wine_table">
                    <thead>
                        <tr className="border-b border-[#8B2433]">
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Sr.no</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Immagine</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Nome Vino</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Quantità</th>
                            {canRemove && <th className="pb-3 px-2 text-center poppins-medium text-[#6B6B6B]">Azione</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {selectedWines && selectedWines.length > 0 ? (
                            selectedWines.map((item, index) => {
                                const wine = item.wine || {};
                                const qty = getDisplayQuantity(item.quantity);

                                return (
                                    <tr key={wine.id || index} className="border-b border-[#8B2433]/12 hover:bg-gray-50/50 transition-colors">
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            {index + 1}
                                        </td>
                                        <td className="pr-2 py-4">
                                            <div 
                                                onClick={() => setSelectedImage(cleanImageUrl(wine.image) || defaultImage)}
                                                className="thumbnail_image rounded-lg w-[50px] h-[60px] bg-white p-1 flex items-center justify-center border border-[#68172394] cursor-pointer hover:shadow-md transition-shadow"
                                                title="Ingrandisci immagine"
                                            >
                                                <img
                                                    className="w-full h-full object-contain"
                                                    src={cleanImageUrl(wine.image) || defaultImage}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = defaultImage;
                                                    }}
                                                    alt={wine.name || "Wine"}
                                                />
                                            </div>
                                        </td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            {wine.name ? (
                                                <div className="relative group inline-block max-w-[220px]">
                                                    <span className="block truncate select-text cursor-text">
                                                        {wine.name}
                                                    </span>
                                                    <div className="absolute opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[70] bottom-full left-0 mb-2 w-max max-w-[320px] bg-[#303030] text-white text-xs rounded-lg py-2 px-3 shadow-xl whitespace-normal break-words select-text pointer-events-auto">
                                                        {wine.name}
                                                        <div className="absolute top-full left-4 border-4 border-transparent border-t-[#303030]"></div>
                                                    </div>
                                                </div>
                                            ) : (
                                                "N/A"
                                            )}
                                        </td>
                                        
                                        {/* Direct Quantity Input Pill */}
                                        <td className="py-4 px-2 text-left">
                                            <div className="inline-block border border-[#F2C3CA] hover:border-[#8B2433] rounded-full px-3 py-1 bg-white shadow-sm transition-colors">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={3}
                                                    value={qty !== undefined && qty !== null ? qty : ""}
                                                    onKeyDown={handleQuantityKeyDown}
                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                    onBlur={() => handleQuantityBlur(index, qty)}
                                                    className="w-12 text-center text-sm font-semibold poppins-medium text-[#DB7685] focus:outline-none"
                                                />
                                            </div>
                                        </td>

                                        {canRemove && (
                                            <td className="py-4 px-2 text-center">
                                                <button
                                                    onClick={() => handleRemoveWine(index)}
                                                    className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all inline-flex items-center justify-center"
                                                    title="Rimuovi Vino"
                                                >
                                                    <RiDeleteBin6Line className="w-5 h-5 text-[#8B2433]" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={canRemove ? 5 : 4} className="text-center py-6 text-gray-500 poppins-regular text-sm bg-white">
                                    Nessun vino trovato
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Image Preview Modal */}
            {selectedImage && createPortal(
                <div 
                    className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" 
                    onClick={() => setSelectedImage(null)}
                >
                    <div 
                        className="relative bg-white rounded-2xl shadow-2xl border border-[#8B2433]/40 p-4 max-w-[85vw] max-h-[85vh] flex items-center justify-center" 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button 
                            className="absolute -top-3 -right-3 bg-[#8B2433] text-white rounded-full w-7 h-7 flex items-center justify-center font-bold shadow-md hover:bg-red-800 transition-colors z-10 cursor-pointer text-xs" 
                            onClick={() => setSelectedImage(null)}
                            title="Chiudi"
                        >
                            ✕
                        </button>
                        <img 
                            src={selectedImage} 
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = defaultImage;
                            }}
                            alt="Wine Preview" 
                            className="max-w-[75vw] max-h-[75vh] object-contain" 
                        />
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

ExistingWinesOrder.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    selectedWines: PropTypes.array.isRequired,
    handleDirectQuantityInput: PropTypes.func.isRequired,
    handleRemoveWine: PropTypes.func.isRequired,
    cleanImageUrl: PropTypes.func.isRequired,
    title: PropTypes.string,
    canRemove: PropTypes.bool
};

export default ExistingWinesOrder;
